import { can, regionLabel, SIDO, type CenterRegion, type MessagingType } from '@selfon/domain/browser';
import { useState } from 'react';
import { api, request, type NoticeRow, type SubscriptionRow } from '../api';
import { useAuth } from '../auth/AuthContext';
import { CardTopup, SubscriptionCard } from '../components/CardPayment';
import { principalOf } from '../components/Layout';
import { Alert, Chip, Dialog, Empty, errorText, Field, Loading, PageHeader, useLoad } from '../components/ui';
import { makeZip, saveBytes } from '../zip';
import { DECISION_LABEL, dateOnly, dateTime, MODULE_LABEL, MSG_TYPE_LABEL, NOTICE_LABEL, PLAN_LABEL, todayKst, won } from '../format';

const PAY_LABEL: Record<string, string> = { PENDING: '결제 전', APPROVING: '승인 중', PAID: '결제 완료', FAILED: '실패', REVIEW: '확인 중', REFUNDED: '환불 완료' };

const SUB_STATUS: Record<SubscriptionRow['status'], { label: string; tone: 'ok' | 'warn' | 'danger' }> = {
  CARD_ONLY: { label: '카드 등록됨', tone: 'ok' }, ACTIVE: { label: '이용 중', tone: 'ok' }, PAST_DUE: { label: '결제 실패', tone: 'danger' },
  LAPSED: { label: '이용 제한', tone: 'danger' }, CANCELED: { label: '해지됨', tone: 'warn' },
};

function SubscriptionFacts({ s }: { s: SubscriptionRow }) {
  return (
    <>
      <dl className="facts facts-2">
        {s.card && <div><dt>카드</dt><dd>{s.card.name} {s.card.num}</dd></div>}
        {s.current && <div><dt>요금</dt><dd>{PLAN_LABEL[s.current.plan]} · {s.current.cycle === 'YEARLY' ? '연' : '월'} {won(s.current.amount)}</dd></div>}
        {s.nextChargeOn && <div><dt>{s.cancelAtPeriodEnd ? '이용 종료일' : '다음 결제일'}</dt><dd>{s.nextChargeOn}</dd></div>}
        {s.next && <div><dt>다음 결제일부터</dt><dd>{PLAN_LABEL[s.next.plan]} · {s.next.cycle === 'YEARLY' ? '연' : '월'} {won(s.next.amount)}</dd></div>}
      </dl>
      {s.status === 'PAST_DUE' && <Alert>결제에 실패했습니다{s.failReason ? `: ${s.failReason}` : ''}. {s.restrictAt ? `${dateOnly(s.restrictAt)}까지 결제되지 않으면 유료 기능 이용이 제한됩니다.` : ''}</Alert>}
      {s.status === 'LAPSED' && <Alert>미납으로 유료 기능 이용이 제한되었습니다. 결제하면 바로 다시 쓸 수 있고, 자료는 그대로 있습니다.</Alert>}
      {s.charging && <Alert kind="info">결제 결과를 확인하고 있습니다.</Alert>}
    </>
  );
}

/** 센터 설정: 현재 요금제·모듈, 요금제 신청(금액 미리보기), 알림톡 발신프로필·템플릿·충전 신청, 신청 내역 */
export function CenterSettings() {
  const { me } = useAuth();
  const p = principalOf(me!);
  const centerId = me!.centerId!;
  const s = useLoad(() => api.settings(centerId), [centerId]);
  const pricing = useLoad(() => api.pricing(), []);
  const pays = useLoad(() => api.payments(centerId), [centerId]);
  const [topup, setTopup] = useState(false);
  const sub = useLoad(() => api.subscription(centerId), [centerId]);
  const [cardOpen, setCardOpen] = useState(false);
  const [subError, setSubError] = useState<string | null>(null);
  const subAct = async (call: () => Promise<unknown>, msg: string) => {
    setSubError(null); setDone(null);
    try { await call(); setDone(msg); sub.reload(); s.reload(); pays.reload(); }
    catch (e) { setSubError(errorText(e)); }
  };
  const [archive, setArchive] = useState<{ month: string; reason: string; unmasked: boolean } | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [plan, setPlan] = useState<{ plan: 'STANDARD' | 'ENTERPRISE_PRO'; cycle: 'MONTHLY' | 'YEARLY' } | null>(null);
  const [msg, setMsg] = useState<{ type: MessagingType; senderNumber: string; kakaoChannelId: string; templateName: string; templateBody: string; creditAmount: number } | null>(null);
  const canRequest = can(p, 'contract:request');
  const data = s.data;
  const pending = data?.contracts.some((c) => c.status === 'PENDING');

  const fee = plan && pricing.data ? pricing.data.plans[plan.plan] * (plan.cycle === 'YEARLY' ? pricing.data.yearlyPaidMonths : 1) : null;

  return (
    <>
      <PageHeader title="센터 설정" />
      {done && <Alert kind="ok">{done}</Alert>}
      {s.error && <Alert>{s.error}</Alert>}
      {s.loading && !data && <Loading />}
      {data?.center.joinCode && (
        <section className="card row" aria-labelledby="join-h">
          <div className="stack" style={{ gap: 4 }}>
            <h2 id="join-h">선생님 가입 코드</h2>
            <p className="muted small">선생님 가입용 코드입니다. 센터 밖으로 공유하지 마세요.</p>
          </div>
          <span className="join-code">{data.center.joinCode}</span>
        </section>
      )}
      {data && me!.role === 'CENTER_DIRECTOR' && !me!.impersonation && <RegionSetting centerId={centerId} />}
      {data && canRequest && (
        <section className="card stack" aria-labelledby="sub-h">
          <div className="row"><h2 id="sub-h">정기결제</h2>
            {sub.data?.subscription && <Chip tone={SUB_STATUS[sub.data.subscription.status]!.tone}>{SUB_STATUS[sub.data.subscription.status]!.label}</Chip>}
          </div>
          {sub.error && <Alert>{sub.error}</Alert>}
          {subError && <Alert>{subError}</Alert>}
          {sub.data && !sub.data.subscription?.hasCard && <p className="muted small">요금제를 신청하려면 카드를 먼저 등록하세요. 등록할 때는 결제되지 않습니다.</p>}
          {sub.data?.subscription && <SubscriptionFacts s={sub.data.subscription} />}
          <div className="row-actions">
            <button type="button" className="btn btn-sm" disabled={Boolean(me!.impersonation)} onClick={() => setCardOpen(true)}>{sub.data?.subscription?.hasCard ? '카드 변경' : '카드 등록'}</button>
            {(sub.data?.subscription?.status === 'PAST_DUE' || sub.data?.subscription?.status === 'LAPSED') && !sub.data.subscription.charging && sub.data.subscription.hasCard && (
              <button type="button" className="btn btn-primary btn-sm" disabled={Boolean(me!.impersonation)} onClick={() => void subAct(() => api.paySubscription(centerId), '결제했습니다.')}>지금 결제</button>
            )}
            {(sub.data?.subscription?.status === 'ACTIVE' || sub.data?.subscription?.status === 'PAST_DUE') && (sub.data.subscription.cancelAtPeriodEnd
              ? <button type="button" className="btn btn-sm" disabled={Boolean(me!.impersonation)} onClick={() => void subAct(() => api.resumeSubscription(centerId), '해지 예약을 취소했습니다.')}>해지 취소</button>
              : <button type="button" className="btn btn-sm" disabled={Boolean(me!.impersonation)} onClick={() => { if (window.confirm('다음 결제일부터 결제하지 않습니다. 결제한 기간까지는 계속 쓸 수 있습니다. 해지할까요?')) void subAct(() => api.cancelSubscription(centerId), '해지를 예약했습니다.'); }}>해지</button>)}
          </div>
        </section>
      )}
      {data && (
        <div className="grid-2">
          <section className="card stack" aria-labelledby="plan-h">
            <div className="row"><h2 id="plan-h">요금제·모듈</h2>
              {canRequest && <button type="button" className="btn btn-primary btn-sm" disabled={pending || !sub.data?.subscription?.hasCard} title={pending ? '심사 중인 신청이 있습니다' : !sub.data?.subscription?.hasCard ? '정기결제 카드를 먼저 등록하세요' : undefined}
                onClick={() => { setDone(null); setPlan({ plan: data.center.plan === 'TRIAL' ? 'STANDARD' : (data.center.plan as 'STANDARD' | 'ENTERPRISE_PRO'), cycle: sub.data?.subscription?.current?.cycle ?? 'MONTHLY' }); }}>요금제·모듈 신청</button>}
            </div>
            <dl className="facts facts-2">
              <div><dt>요금제</dt><dd>{PLAN_LABEL[data.center.plan]}{data.center.plan === 'TRIAL' ? (data.center.trialEndsAt && data.center.trialEndsAt > new Date().toISOString() ? ` (${dateOnly(data.center.trialEndsAt)}까지)` : ' (기간 끝남)') : ''}</dd></div>
              <div><dt>계약 상태</dt><dd>{data.center.status === 'PAID' ? '유료계약' : data.center.status === 'TRIAL' ? '무료체험' : '일시정지'}</dd></div>
            </dl>
            <div className="chips">{data.center.modules.map((m) => <Chip key={m} tone="info">{MODULE_LABEL[m]}</Chip>)}</div>
            <h3>신청 내역</h3>
            {data.contracts.length === 0 ? <p className="muted small">신청 내역이 없습니다.</p> : (
              <ul className="plain">
                {data.contracts.map((c) => (
                  <li key={c.id} className="history">
                    <span>{dateOnly(c.requestedAt)} · {PLAN_LABEL[c.plan]}{c.addModules.length ? ` + ${c.addModules.map((m) => MODULE_LABEL[m]).join(', ')}` : ''} · 월 {won(c.monthlyFee)}</span>
                    <Chip tone={DECISION_LABEL[c.status]!.tone}>{DECISION_LABEL[c.status]!.label}</Chip>
                    {c.rejectReason && <p className="muted small">반려 사유: {c.rejectReason}</p>}
                  </li>
                ))}
              </ul>
            )}
          </section>

          {can(p, 'export:download') && (
            <section className="card stack" aria-labelledby="archive-h">
              <h2 id="archive-h">센터 자료 내려받기</h2>
              <p className="muted small">한 달 치 일정·출결·수납·감사 로그를 압축 파일로 받습니다. 사유가 감사 로그에 남고, 민감정보는 포함되지 않습니다.</p>
              <div><button type="button" className="btn btn-outline" onClick={() => { setDone(null); setArchive({ month: todayKst().slice(0, 7), reason: '', unmasked: false }); }}>자료 내려받기</button></div>
            </section>
          )}

          <section className="card stack" aria-labelledby="msg-h">
            <h2 id="msg-h">알림톡·문자</h2>
            <dl className="facts facts-2">
              <div><dt>발신프로필</dt><dd>{data.messaging.profileActive ? `${data.messaging.kakaoChannelId} · ${data.messaging.senderNumber}` : '미등록'}</dd></div>
              <div><dt>잔여 포인트</dt><dd>{data.messaging.credits.toLocaleString('ko-KR')} P</dd></div>
            </dl>
            {data.messaging.templates.length > 0 && (
              <div><h3>승인된 템플릿</h3><ul className="plain">{data.messaging.templates.map((t) => <li key={t.id}><strong>{t.name}</strong><p className="small template-body">{t.body}</p></li>)}</ul></div>
            )}
            {canRequest && (
              <div className="btn-row">
                {!data.center.modules.includes('MESSAGING') && <p className="hint">알림톡 모듈이 없습니다. 왼쪽에서 모듈을 함께 신청하세요.</p>}
                <button type="button" className="btn btn-outline btn-sm" disabled={!data.center.modules.includes('MESSAGING')}
                  onClick={() => { setDone(null); setMsg({ type: 'SENDER_PROFILE', senderNumber: '', kakaoChannelId: '@', templateName: '', templateBody: '', creditAmount: 30000 }); }}>발신프로필 신청</button>
                <button type="button" className="btn btn-outline btn-sm" disabled={!data.messaging.profileActive}
                  onClick={() => { setDone(null); setMsg({ type: 'TEMPLATE', senderNumber: '', kakaoChannelId: '', templateName: '', templateBody: '안녕하세요 #{보호자명}님, #{센터명}입니다. ', creditAmount: 30000 }); }}>템플릿 검수 신청</button>
                <button type="button" className="btn btn-primary btn-sm" disabled={!pays.data || Boolean(me!.impersonation) || !data.messaging.profileActive}
                  onClick={() => { setDone(null); setTopup(true); }}>카드로 포인트 충전</button>
                {data.center.modules.includes('MESSAGING') && !data.messaging.profileActive && <p className="hint">포인트는 발신프로필이 승인된 뒤 충전할 수 있습니다. 승인 전에는 알림톡을 보낼 수 없습니다.</p>}
              </div>
            )}
            <h3>카드 결제 내역</h3>
            {pays.error && <Alert>{pays.error}</Alert>}
            {pays.data && (pays.data.payments.filter((x) => x.status !== 'PENDING').length === 0 ? <p className="muted small">결제 내역이 없습니다.</p> : (
              <ul className="plain">
                {pays.data.payments.filter((x) => x.status !== 'PENDING').map((x) => (
                  <li key={x.id} className="history">
                    <span>{dateTime(x.paidAt ?? x.createdAt)} · {x.kind === 'SUBSCRIPTION' ? x.goods : `${x.credits.toLocaleString('ko-KR')}P`} · {won(x.amount)}{x.receipt?.cardName ? ` · ${x.receipt.cardName} ${x.receipt.cardNum ?? ''}` : ''}</span>
                    <Chip tone={x.status === 'PAID' ? 'ok' : x.status === 'FAILED' ? 'danger' : 'warn'}>{PAY_LABEL[x.status]}</Chip>
                    {x.receipt?.receiptUrl && <a className="small" href={x.receipt.receiptUrl} target="_blank" rel="noopener noreferrer">영수증</a>}
                    {x.refundedAmount > 0 && <p className="muted small">{won(x.refundedAmount)} 환불{x.refundedAt ? ` · ${dateTime(x.refundedAt)}` : ''}</p>}
                    {x.failReason && <p className="muted small">{x.failReason}</p>}
                  </li>
                ))}
              </ul>
            ))}
            <h3>신청 내역</h3>
            {data.messagingRequests.length === 0 ? <p className="muted small">신청 내역이 없습니다.</p> : (
              <ul className="plain">
                {data.messagingRequests.map((r) => (
                  <li key={r.id} className="history">
                    <span>{dateOnly(r.requestedAt)} · {MSG_TYPE_LABEL[r.type]}{r.templateName ? ` · ${r.templateName}` : ''}{r.creditAmount ? ` · ${r.creditAmount.toLocaleString('ko-KR')}P` : ''}</span>
                    <Chip tone={DECISION_LABEL[r.status]!.tone}>{DECISION_LABEL[r.status]!.label}</Chip>
                    {r.rejectReason && <p className="muted small">반려 사유: {r.rejectReason}</p>}
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      )}

      <Dialog wide title="요금제·모듈 신청" open={Boolean(plan)} onClose={() => setPlan(null)} submitLabel="신청"
        onSubmit={async () => { await api.requestContract(centerId, plan!); setDone('신청했습니다. 본사 심사 후 적용됩니다.'); s.reload(); }}>
        {plan && pricing.data && <>
          <fieldset className="radios">
            <legend>요금제</legend>
            {(['STANDARD', 'ENTERPRISE_PRO'] as const).map((k) => (
              <label key={k}><input type="radio" name="plan" checked={plan.plan === k} onChange={() => setPlan({ ...plan, plan: k })} />
                {PLAN_LABEL[k]} · 월 {won(pricing.data!.plans[k])} <span className="muted small">(선생님 {pricing.data!.teacherLimits[k]}명까지 · {pricing.data!.planModules[k].map((m) => MODULE_LABEL[m]).join(', ')})</span></label>
            ))}
          </fieldset>
          <fieldset className="radios">
            <legend>결제 주기</legend>
            <label><input type="radio" name="cycle" checked={plan.cycle === 'MONTHLY'} onChange={() => setPlan({ ...plan, cycle: 'MONTHLY' })} /> 매월</label>
            <label><input type="radio" name="cycle" checked={plan.cycle === 'YEARLY'} onChange={() => setPlan({ ...plan, cycle: 'YEARLY' })} /> 매년 <span className="muted small">(2개월 무료)</span></label>
          </fieldset>
          {fee !== null && <p className="preview-line"><strong>{plan.cycle === 'YEARLY' ? '연 결제 금액' : '월 결제 금액'}</strong> {won(fee)} (부가세 포함, 최종 금액은 서버가 다시 계산)</p>}
        </>}
      </Dialog>

      <Dialog wide title={msg ? MSG_TYPE_LABEL[msg.type]! : ''} open={Boolean(msg)} onClose={() => setMsg(null)} submitLabel="신청"
        onSubmit={async () => {
          const b = msg!;
          await api.requestMessaging(centerId, b.type === 'SENDER_PROFILE' ? { type: b.type, senderNumber: b.senderNumber, kakaoChannelId: b.kakaoChannelId }
            : b.type === 'TEMPLATE' ? { type: b.type, templateName: b.templateName, templateBody: b.templateBody } : { type: b.type, creditAmount: b.creditAmount });
          setDone(`'${MSG_TYPE_LABEL[b.type]}' 신청을 접수했습니다. 본사 승인 후 적용됩니다.`); s.reload();
        }}>
        {msg?.type === 'SENDER_PROFILE' && <div className="form-grid">
          <Field id="m-ch" label="카카오 채널 검색용 아이디"><input id="m-ch" required value={msg.kakaoChannelId} onChange={(e) => setMsg({ ...msg, kakaoChannelId: e.target.value })} placeholder="@햇살아동발달" /></Field>
          <Field id="m-num" label="발신번호(사전 등록된 대표번호)"><input id="m-num" required inputMode="tel" value={msg.senderNumber} onChange={(e) => setMsg({ ...msg, senderNumber: e.target.value })} placeholder="02-0000-0000" /></Field>
        </div>}
        {msg?.type === 'TEMPLATE' && <>
          <Field id="m-tn" label="템플릿 이름"><input id="m-tn" required minLength={2} maxLength={40} value={msg.templateName} onChange={(e) => setMsg({ ...msg, templateName: e.target.value })} placeholder="수업 일정 안내" /></Field>
          <Field id="m-tb" label="템플릿 내용" hint={`쓸 수 있는 변수: ${(pricing.data?.templateVariables ?? []).join(' ')} — 장애·진단 등 민감정보는 넣을 수 없습니다.`}>
            <textarea id="m-tb" rows={5} required minLength={10} maxLength={1000} value={msg.templateBody} onChange={(e) => setMsg({ ...msg, templateBody: e.target.value })} aria-describedby="m-tb-hint" />
          </Field>
        </>}
        {msg?.type === 'CREDIT_TOPUP' && (
          <fieldset className="radios">
            <legend>충전 금액</legend>
            {(pricing.data?.creditPacks ?? []).map((n) => <label key={n}><input type="radio" name="credit" checked={msg.creditAmount === n} onChange={() => setMsg({ ...msg, creditAmount: n })} /> {n.toLocaleString('ko-KR')}P ({won(n)})</label>)}
          </fieldset>
        )}
      </Dialog>
      <Dialog title="센터 자료 내려받기" open={Boolean(archive)} onClose={() => setArchive(null)} submitLabel="압축 파일 받기"
        onSubmit={async () => {
          const { files } = await api.exportArchive(centerId, archive!);
          saveBytes(`SelfOn_${archive!.month}.zip`, makeZip(files), 'application/zip');
          setDone(`${archive!.month} 자료를 내려받았습니다. 안전한 곳에 보관하고 메일이나 메신저로 보내지 마세요.`);
        }}>
        {archive && <>
          <Field id="ar-month" label="월"><input id="ar-month" type="month" required value={archive.month} onChange={(e) => setArchive({ ...archive, month: e.target.value })} /></Field>
          <Field id="ar-reason" label="내려받는 사유 (5자 이상)"><input id="ar-reason" required minLength={5} maxLength={200} value={archive.reason} onChange={(e) => setArchive({ ...archive, reason: e.target.value })} /></Field>
          <label><input type="checkbox" checked={archive.unmasked} onChange={(e) => setArchive({ ...archive, unmasked: e.target.checked })} /> 이름을 가리지 않고 받기</label>
        </>}
      </Dialog>
      <SubscriptionCard centerId={centerId} open={cardOpen} onClose={() => setCardOpen(false)} onDone={(m) => { setDone(m); sub.reload(); }} />
      {pays.data && (
        <CardTopup centerId={centerId} packs={pays.data.creditPacks} mode={pays.data.mode} open={topup} onClose={() => setTopup(false)}
          onDone={(m) => { setDone(m); s.reload(); pays.reload(); }} />
      )}
    </>
  );
}

/** 센터 화면의 공지(본사)·1:1 문의. 공지를 펼치면 열람으로 기록된다. 문의는 센터장만. */
export function CenterNotices() {
  const { me } = useAuth();
  const p = principalOf(me!);
  const centerId = me!.centerId!;
  const notices = useLoad(() => api.notices(), []);
  const canInquire = can(p, 'inquiry:create') || Boolean(me!.impersonation);
  const inquiries = useLoad(() => (canInquire ? api.inquiries(centerId) : Promise.resolve(null)), [centerId, canInquire]);
  const [writing, setWriting] = useState(false);
  const [form, setForm] = useState({ title: '', body: '' });
  const [done, setDone] = useState<string | null>(null);
  const [readNow, setReadNow] = useState<Set<string>>(new Set());

  const open = async (n: NoticeRow) => {
    if (n.read || readNow.has(n.id)) return;
    setReadNow(new Set(readNow).add(n.id));
    try { await api.readNotice(n.id); } catch { /* 열람 기록 실패는 화면 사용을 막지 않는다 */ }
  };

  return (
    <>
      <PageHeader title="공지·문의"
        actions={can(p, 'inquiry:create') && <button type="button" className="btn btn-primary" onClick={() => { setForm({ title: '', body: '' }); setDone(null); setWriting(true); }}>1:1 문의하기</button>} />
      {done && <Alert kind="ok">{done}</Alert>}
      <section className="stack" aria-labelledby="c-notice-h">
        <h2 id="c-notice-h">본사 공지</h2>
        {notices.error && <Alert>{notices.error}</Alert>}
        {notices.loading && !notices.data && <Loading />}
        {notices.data?.notices.length === 0 && <Empty>공지가 없습니다.</Empty>}
        <ul className="notice-list">
          {(notices.data?.notices ?? []).map((n) => {
            const unread = !n.read && !readNow.has(n.id);
            return (
              <li key={n.id} className="card">
                <details onToggle={(e) => { if ((e.target as HTMLDetailsElement).open) open(n); }}>
                  <summary>
                    <Chip tone={NOTICE_LABEL[n.category]!.tone}>{NOTICE_LABEL[n.category]!.label}</Chip>{' '}
                    <strong>{n.title}</strong> <span className="muted small">· {dateOnly(n.publishedAt)}</span>
                    {unread && <> <span className="new-dot" aria-label="읽지 않음">새 공지</span></>}
                  </summary>
                  <p className="pre small notice-body">{n.body}</p>
                </details>
              </li>
            );
          })}
        </ul>
      </section>

      {canInquire && (
        <section className="stack" aria-labelledby="c-inq-h">
          <h2 id="c-inq-h">1:1 문의</h2>
          {inquiries.error && <Alert>{inquiries.error}</Alert>}
          {inquiries.data?.inquiries.length === 0 && <Empty>남긴 문의가 없습니다.</Empty>}
          <ul className="inquiry-list">
            {(inquiries.data?.inquiries ?? []).map((q) => (
              <li key={q.id} className="card stack">
                <div className="row"><span><strong>{q.title}</strong> <span className="muted small">· {dateTime(q.createdAt)}</span></span>
                  <Chip tone={q.status === 'OPEN' ? 'warn' : 'ok'}>{q.status === 'OPEN' ? '답변 대기' : '답변 완료'}</Chip></div>
                <p className="pre small">{q.body}</p>
                {q.answer && <div className="answer"><strong className="small">본사 답변 · {q.answeredAt ? dateTime(q.answeredAt) : ''}</strong><p className="pre small">{q.answer}</p></div>}
              </li>
            ))}
          </ul>
        </section>
      )}

      <Dialog wide title="1:1 문의" open={writing} onClose={() => setWriting(false)} submitLabel="문의 등록"
        onSubmit={async () => { await api.createInquiry(centerId, form); setDone('문의를 등록했습니다. 본사가 답변하면 이 화면에 표시됩니다.'); inquiries.reload(); }}>
        <Field id="q-title" label="제목"><input id="q-title" required minLength={2} maxLength={120} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></Field>
        <Field id="q-body" label="내용" hint="아동 이름·연락처 같은 개인정보는 적지 마세요.">
          <textarea id="q-body" rows={6} required minLength={5} maxLength={4000} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} aria-describedby="q-body-hint" />
        </Field>
      </Dialog>
    </>
  );
}

/** 센터 지역(시·도 + 시·군·구): 정하면 같은 지역 센터 보호자와 함께 보는 지역 게시판과 매일 아침 지역 교육·의료 정보보고가 열린다. 원장 본인만. */
function RegionSetting({ centerId }: { centerId: string }) {
  const q = useLoad(() => request<{ region: CenterRegion | null }>('GET', `/centers/${centerId}/region`), [centerId]);
  const [form, setForm] = useState<{ sido: string; sigungu: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const cur = q.data?.region ?? null;
  const f = form ?? { sido: cur?.sido ?? '', sigungu: cur?.sigungu ?? '' };
  const changed = f.sido !== (cur?.sido ?? '') || f.sigungu.trim() !== (cur?.sigungu ?? '');
  const save = async () => {
    setBusy(true); setError(null); setNotice(null);
    try {
      const r = await request<{ region: CenterRegion }>('POST', `/centers/${centerId}/region`, { sido: f.sido, sigungu: f.sigungu.trim() });
      setNotice(`센터 지역을 ${regionLabel(r.region)}(으)로 정했습니다. 같은 지역 센터 보호자와 함께 보는 지역 게시판이 열립니다.`);
      setForm(null); q.reload();
    } catch (e) { setError(errorText(e)); } finally { setBusy(false); }
  };
  return (
    <section className="card stack" aria-labelledby="region-h">
      <div className="stack" style={{ gap: 4 }}>
        <h2 id="region-h">센터 지역</h2>
        <p className="muted small">정하면 "오늘 있었던 일"에 같은 지역 센터 보호자가 함께 보는 지역 게시판이 열리고, 매일 아침 지역 교육·의료 정보보고가 올라옵니다. 주소 전체가 아니라 시·군·구까지만 씁니다.</p>
      </div>
      {q.error && <Alert>{q.error}</Alert>}
      {notice && <Alert kind="ok">{notice}</Alert>}
      {error && <Alert>{error}</Alert>}
      {q.data && (
        <form className="grid-2" onSubmit={(e) => { e.preventDefault(); void save(); }}>
          <Field id="rg-sido" label="시·도">
            <select id="rg-sido" required value={f.sido} onChange={(e) => setForm({ ...f, sido: e.target.value })}>
              <option value="">고르세요</option>
              {SIDO.map((x) => <option key={x} value={x}>{x}</option>)}
            </select>
          </Field>
          <Field id="rg-sigungu" label="시·군·구" hint="예: 충주시, 괴산군, 수원시 영통구">
            <input id="rg-sigungu" required maxLength={30} value={f.sigungu} onChange={(e) => setForm({ ...f, sigungu: e.target.value })} />
          </Field>
          <div className="row">
            <button type="submit" className="btn btn-primary" disabled={busy || !changed || !f.sido || !f.sigungu.trim()}>{busy ? '저장 중…' : cur ? '지역 바꾸기' : '지역 정하기'}</button>
            {cur && <span className="muted small">지금: {regionLabel(cur)}</span>}
          </div>
        </form>
      )}
    </section>
  );
}
